<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { applyDemoStep, initialCaseState, statusNames, type ToolStatus } from './domain/case'

const menuOpen = ref(false)
const scrolled = ref(false)
const caseState = ref(initialCaseState())
const demoStep = ref(0)
const activeCaseTab = ref<'history' | 'ledger' | 'inbound' | 'outbound'>('history')
const keyword = ref('')
const selectedStatus = ref<'' | ToolStatus>('')

const navItems = [
  { label: '真实案例', href: '#case' },
  { label: '方法', href: '#method' },
  { label: '产品与服务', href: '#service' },
  { label: '效果与证据', href: '#outcome' },
  { label: '交付托管', href: '#delivery' },
]

const pipeline = [
  { id: '01', name: '价值定义', en: 'VALUE', desc: '明确谁在什么场景遇到什么问题，以及成功指标。', output: '业务问题画布' },
  { id: '02', name: '遗留调查', en: 'EVIDENCE', desc: '采集界面、Delphi 源码、SQL、手册和用户经验。', output: '证据包与清单' },
  { id: '03', name: '知识重建', en: 'MODEL', desc: '区分观察事实、AI 推断与业务确认，重建流程和规则。', output: '用例、规则与模型' },
  { id: '04', name: '行为冻结', en: 'SPECIFY', desc: '把旧系统关键结果固化为 Golden Master 与验收场景。', output: '可执行规格' },
  { id: '05', name: '现代实现', en: 'ENGINEER', desc: '按 API 契约实现 Vue + Spring Boot 纵向切片。', output: '候选软件变更' },
  { id: '06', name: '独立验证', en: 'VERIFY', desc: '运行单元、契约、差异和端到端测试，闭环所有差异。', output: '验证证据' },
  { id: '07', name: '审批发布', en: 'RELEASE', desc: '经人类评审、CI Gate、灰度与回滚演练后发布。', output: '可回滚制品' },
  { id: '08', name: '价值验收', en: 'YIELD', desc: '采集性能、质量与用户效率数据，决定下一轮优化。', output: '运行与价值证据' },
]

const serviceStages = [
  { week: '01', title: '迁移发现与风险画像', duration: '1–2 周', desc: '选择低风险纵向切片，建立业务问题、资产清单、迁移边界和风险基线。', deliverables: ['Business Problem Canvas', 'Legacy Evidence Pack', '试点范围与成功指标'] },
  { week: '02', title: '可验证迁移试点', duration: '3–6 周', desc: '完成一个真实模块从证据、规则、测试到现代 Web 系统的闭环。', deliverables: ['Vue + Spring Boot 切片', 'Golden Master / 差异测试', '灰度与回滚方案'] },
  { week: '03', title: '企业软件工厂部署', duration: '4–8 周', desc: '接入企业代码平台、Runner、制品库、Agent Gateway 与测试环境。', deliverables: ['标准工作站与 devctl', 'CI/CD 与策略 Gate', '审计、观测和发布链路'] },
  { week: '04', title: '团队能力迁移', duration: '15 天 + 实战', desc: '用第二个模块检验团队能否脱离示范者独立复用方法。', deliverables: ['分层培训与实验', '晋级 Gate', 'Transfer Challenge 评估'] },
]

const demoSteps = [
  { title: '创建入库记录', detail: '新工装 GX-2026-042 / D-006 进入总装一库。' },
  { title: '执行外借出库', detail: '状态机校验“在库 → 外借”，旧状态转为历史版本。' },
  { title: '核对台账明细', detail: '验证最新状态唯一，并保留完整操作链。' },
  { title: '形成运行证据', detail: '记录业务结果、审计轨迹与可复验结论。' },
]

const displayedRecords = computed(() => caseState.value.records.filter((record) => {
  const matchesTab = activeCaseTab.value === 'history'
    ? true
    : activeCaseTab.value === 'ledger'
      ? record.isLatest
      : activeCaseTab.value === 'inbound'
        ? record.status === 1
        : record.status !== 1
  const term = keyword.value.trim().toLowerCase()
  const matchesKeyword = !term || `${record.drawingNo} ${record.serialNo} ${record.warehouse}`.toLowerCase().includes(term)
  const matchesStatus = selectedStatus.value === '' || record.status === selectedStatus.value
  return matchesTab && matchesKeyword && matchesStatus
}))

const activeCaseTitle = computed(() => ({
  history: '存储历史管理',
  ledger: '工装台账',
  inbound: '入库列表',
  outbound: '出库列表',
})[activeCaseTab.value])

function runDemoStep() {
  if (demoStep.value >= demoSteps.length) {
    resetDemo()
    return
  }
  const nextStep = demoStep.value + 1
  caseState.value = applyDemoStep(caseState.value, nextStep)
  demoStep.value = nextStep
  if (nextStep === 1) activeCaseTab.value = 'inbound'
  if (nextStep === 2) activeCaseTab.value = 'outbound'
  if (nextStep === 3) activeCaseTab.value = 'ledger'
}

function resetDemo() {
  caseState.value = initialCaseState()
  demoStep.value = 0
  activeCaseTab.value = 'history'
  keyword.value = ''
  selectedStatus.value = ''
}

function closeMenu() {
  menuOpen.value = false
}

function handleScroll() {
  scrolled.value = window.scrollY > 24
}

onMounted(() => {
  handleScroll()
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onBeforeUnmount(() => window.removeEventListener('scroll', handleScroll))
</script>

<template>
  <div class="site-shell">
    <header class="topbar" :class="{ 'topbar--scrolled': scrolled }">
      <a class="brand" href="#top" aria-label="返回首页" @click="closeMenu">
        <span class="brand-mark"><i></i><i></i><i></i></span>
        <span><b>AI-Native</b><small>SOFTWARE FACTORY</small></span>
      </a>
      <nav class="desktop-nav" aria-label="主导航">
        <a v-for="item in navItems" :key="item.href" :href="item.href">{{ item.label }}</a>
      </nav>
      <a class="topbar-cta" href="#delivery">查看交付方案 <span>↗</span></a>
      <button class="menu-button" type="button" :aria-expanded="menuOpen" aria-label="打开导航" @click="menuOpen = !menuOpen">
        <span></span><span></span>
      </button>
      <nav v-if="menuOpen" class="mobile-nav" aria-label="移动端导航">
        <a v-for="item in navItems" :key="item.href" :href="item.href" @click="closeMenu">{{ item.label }}</a>
      </nav>
    </header>

    <main id="top">
      <section class="hero section-pad">
        <div class="hero-glow"></div>
        <div class="hero-copy reveal">
          <p class="eyebrow"><span></span> 从遗留证据到业务价值</p>
          <h1>不是更快地重写代码，<br /><em>而是正确地重建系统。</em></h1>
          <p class="hero-lead">我们把隐含在 Delphi 界面、事件与 SQL 中的业务知识，转化为可审查需求、可执行规格、现代 Web 服务和可持续演化的工程能力。</p>
          <div class="hero-actions">
            <a class="button button--primary" href="#case">体验真实案例 <span>→</span></a>
            <a class="button button--ghost" href="#method"><span class="play">▶</span> 了解完整方法</a>
          </div>
          <div class="hero-proof">
            <div><b>证据驱动</b><span>每个结论都有来源</span></div>
            <div><b>独立验证</b><span>AI 生成，人和工具验收</span></div>
            <div><b>渐进替换</b><span>可灰度、可比较、可回滚</span></div>
          </div>
        </div>

        <div class="hero-visual" aria-label="从遗留系统到现代软件的转换示意图">
          <div class="visual-grid"></div>
          <div class="legacy-card floating-card">
            <div class="card-kicker"><span class="window-dots"><i></i><i></i><i></i></span> LEGACY / DELPHI</div>
            <pre><span>procedure</span> btnQueryClick;
<b>begin</b>
  qry.SQL.Add(<i>'SELECT...'</i>);
  <u>if Status = 'LOCKED'</u> then
    btnOrder.Enabled := False;
<b>end;</b></pre>
            <div class="legacy-meta"><span>UI 事件</span><span>业务规则</span><span>内嵌 SQL</span></div>
          </div>
          <div class="transform-line"><span>知识重建</span><i>→</i></div>
          <div class="modern-card floating-card">
            <div class="card-kicker"><span class="pulse"></span> VERIFIED / MODERN</div>
            <div class="stack-row"><span>Vue 3</span><i></i><small>交互与体验</small></div>
            <div class="stack-row"><span>OpenAPI</span><i></i><small>可执行契约</small></div>
            <div class="stack-row"><span>Spring Boot</span><i></i><small>业务服务</small></div>
            <div class="stack-row"><span>Existing DB</span><i></i><small>渐进式迁移</small></div>
            <div class="verified-stamp">✓ VERIFIED CHANGE</div>
          </div>
          <div class="orbit orbit--one">AC</div>
          <div class="orbit orbit--two">CI</div>
          <div class="orbit orbit--three">MR</div>
        </div>
      </section>

      <section class="fact-strip" aria-label="参考案例事实">
        <div class="fact-intro"><span>参考实现</span><b>公开案例中的<br />可核验事实</b></div>
        <div class="fact"><strong>4</strong><span>个核心业务页面</span></div>
        <div class="fact"><strong>21</strong><span>张遗留表完成映射</span></div>
        <div class="fact"><strong>11</strong><span>个状态编码被保留</span></div>
        <div class="fact"><strong>7</strong><span>项核心后端测试通过</span></div>
        <a class="fact-link" href="https://github.com/tjluo-ucas/tjluo-ucas.github.io/blob/master/docs/CASE-STUDY.md" target="_blank" rel="noreferrer">核对案例说明 ↗</a>
      </section>

      <section id="case" class="case-section section-pad">
        <div class="section-heading section-heading--split">
          <div>
            <p class="eyebrow eyebrow--dark"><span></span> 实际应用案例 / 01</p>
            <h2>工装库房管控：<br />让每次状态变化都有依据</h2>
          </div>
          <p>参考系统把 Delphi 工装台账与出入库模块迁移为浏览器可访问的现代应用。下面用一条真实业务路径，展示产品如何工作，而不只展示技术名词。</p>
        </div>

        <div class="case-context">
          <article><span>用户</span><b>库房管理员 / 工艺人员</b><p>无需依赖旧 Windows 客户端，统一查询和处理工装状态。</p></article>
          <article><span>问题</span><b>状态分散，历史难追溯</b><p>业务逻辑藏在事件和 SQL 中，修改风险高、验证困难。</p></article>
          <article><span>方法</span><b>状态机 + 版本历史</b><p>每次操作生成新版本，旧记录保留，最新状态唯一。</p></article>
          <article><span>价值</span><b>可查询、可审计、可演化</b><p>把个人经验变成系统规则，把黑盒行为变成验证证据。</p></article>
        </div>

        <div class="product-demo">
          <aside class="demo-guide">
            <p class="demo-label">GUIDED PRODUCT TOUR</p>
            <h3>用 4 步看懂一次完整业务闭环</h3>
            <ol>
              <li v-for="(step, index) in demoSteps" :key="step.title" :class="{ active: demoStep === index, done: demoStep > index }">
                <span>{{ demoStep > index ? '✓' : index + 1 }}</span>
                <div><b>{{ step.title }}</b><small>{{ step.detail }}</small></div>
              </li>
            </ol>
            <button class="button button--demo" type="button" @click="runDemoStep">
              {{ demoStep >= demoSteps.length ? '重新运行演示' : demoStep === 0 ? '开始业务演示' : '执行下一步' }} <span>→</span>
            </button>
            <button v-if="demoStep > 0" class="reset-link" type="button" @click="resetDemo">重置数据</button>
          </aside>

          <div class="demo-window">
            <div class="demo-window-bar">
              <div class="window-dots"><i></i><i></i><i></i></div>
              <div class="address-bar"><span>⌕</span> factory.local / equipment-ledger</div>
              <div class="secure-dot"></div>
            </div>
            <div class="app-shell">
              <div class="app-topline">
                <div class="app-brand"><span>F</span><b>工装库房管控</b></div>
                <div class="app-user"><span>陈</span><small>库房管理员</small></div>
              </div>
              <nav class="app-tabs">
                <button v-for="tab in ([['history','存储历史'],['ledger','台账'],['inbound','入库列表'],['outbound','出库列表']] as const)" :key="tab[0]" :class="{ active: activeCaseTab === tab[0] }" type="button" @click="activeCaseTab = tab[0]">{{ tab[1] }}</button>
              </nav>
              <div class="app-content">
                <div class="app-heading"><div><small>工装全生命周期</small><h4>{{ activeCaseTitle }}</h4></div><span class="sync-state"><i></i> 演示环境</span></div>
                <div class="app-filters">
                  <label><span>图号 / 编号</span><input v-model="keyword" placeholder="输入关键词查询" /></label>
                  <label><span>状态</span><select v-model="selectedStatus"><option value="">全部状态</option><option v-for="(name, code) in statusNames" :key="code" :value="Number(code)">{{ name }}</option></select></label>
                  <button type="button">查询</button>
                </div>
                <div class="app-summary"><span>共 <b>{{ displayedRecords.length }}</b> 条记录</span><span v-if="demoStep > 0" class="latest-event">✓ {{ caseState.audit[0] }}</span></div>
                <div class="data-table-wrap">
                  <table>
                    <thead><tr><th>图号</th><th>编号</th><th>库房 / 架位</th><th>状态</th><th>操作日期</th><th>操作人</th><th>版本</th></tr></thead>
                    <tbody>
                      <tr v-for="record in displayedRecords" :key="record.id" :class="{ muted: !record.isLatest, highlight: record.drawingNo === 'GX-2026-042' && record.isLatest }">
                        <td><b>{{ record.drawingNo }}</b></td><td>{{ record.serialNo }}</td><td>{{ record.warehouse }}<small>{{ record.shelf }}</small></td>
                        <td><span class="status-pill" :data-status="record.status">{{ statusNames[record.status] }}</span></td>
                        <td>{{ record.operatedAt }}</td><td>{{ record.operator }}</td><td><span class="version-pill" :class="{ current: record.isLatest }">{{ record.isLatest ? '最新' : '历史' }}</span></td>
                      </tr>
                      <tr v-if="displayedRecords.length === 0"><td colspan="7" class="empty-state">没有符合条件的演示记录</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="case-insight">
          <span class="quote-mark">“</span>
          <p>关键成果不是页面从桌面端搬到了浏览器，而是 <b>opt_type 状态规则</b> 和 <b>islast 版本语义</b> 从隐含实现变成了可说明、可测试、可审计的系统知识。</p>
          <a href="https://delph-restructuring-test-frontend.onrender.com/" target="_blank" rel="noreferrer">访问原参考系统 ↗</a>
        </div>
      </section>

      <section id="method" class="method-section section-pad">
        <div class="section-heading section-heading--light">
          <p class="eyebrow"><span></span> VALUE-FACTORY 方法</p>
          <h2>代码只是中间产物。<br />可验证的价值闭环才是交付。</h2>
          <p>每一个阶段都有输入、输出与人工 Gate。AI 可以推断和生成，但不能把推断变成事实，也不能绕过独立验证进入生产。</p>
        </div>
        <div class="pipeline">
          <article v-for="item in pipeline" :key="item.id">
            <div class="pipeline-top"><span>{{ item.id }}</span><small>{{ item.en }}</small></div>
            <h3>{{ item.name }}</h3>
            <p>{{ item.desc }}</p>
            <div class="pipeline-output"><i></i> {{ item.output }}</div>
          </article>
        </div>
        <div class="governance-band">
          <div><span>治理原则</span><h3>AI 生成候选，<br />人类与确定性工具负责验证。</h3></div>
          <div class="gate-flow">
            <span>Observed<small>已观察</small></span><i>→</i><span>Inferred<small>AI 推断</small></span><i>→</i><span>Reviewed<small>业务审查</small></span><i>→</i><span>Executable<small>可执行</small></span>
          </div>
        </div>
      </section>

      <section id="service" class="service-section section-pad">
        <div class="section-heading section-heading--split">
          <div><p class="eyebrow eyebrow--dark"><span></span> 产品与服务</p><h2>从一个可控试点，<br />长成企业的软件生产能力。</h2></div>
          <p>服务不是一次性交付代码，而是沿着“试点—平台—组织”逐步降低不确定性。每一阶段都能独立验收，也能在价值不成立时及时止损。</p>
        </div>
        <div class="service-list">
          <article v-for="stage in serviceStages" :key="stage.week">
            <div class="stage-no">{{ stage.week }}</div>
            <div class="stage-main"><span>{{ stage.duration }}</span><h3>{{ stage.title }}</h3><p>{{ stage.desc }}</p></div>
            <ul><li v-for="item in stage.deliverables" :key="item">{{ item }}</li></ul>
          </article>
        </div>
        <div class="architecture-card">
          <div class="arch-copy"><span>SYSTEM BLUEPRINT</span><h3>目标系统不是一个“AI 插件”，而是一条受控的软件生产线。</h3><p>业务负责人定义价值；开发者与受控 Agent 产生候选变更；代码平台、测试 Runner 和人工评审共同形成发布证据；生产只接收已批准制品。</p></div>
          <div class="arch-diagram">
            <div class="arch-node arch-node--business"><small>01</small><b>业务 / 产品 Owner</b><span>需求 · 验收 · 价值</span></div>
            <div class="arch-arrow">↓</div>
            <div class="arch-node arch-node--factory"><small>02</small><b>Software Factory</b><span>Issue · MR · CI/CD · Audit</span></div>
            <div class="arch-split"><i></i><i></i><i></i></div>
            <div class="arch-leaves"><div><b>Developer + Agent</b><span>受控实现</span></div><div><b>Verification Runner</b><span>独立验证</span></div><div><b>Artifact + Runtime</b><span>发布与证据</span></div></div>
          </div>
        </div>
      </section>

      <section id="outcome" class="outcome-section section-pad">
        <div class="section-heading section-heading--light section-heading--split">
          <div><p class="eyebrow"><span></span> 效果与证据</p><h2>不以生成了多少代码衡量 AI，<br />只看交付了多少已验证变化。</h2></div>
          <p>演示数据说明“系统做了什么”；项目指标回答“业务是否因此变好”。两类证据必须分开记录。</p>
        </div>
        <div class="evidence-grid">
          <article class="evidence-card evidence-card--verified"><span class="evidence-tag">本参考实现已验证</span><h3>工程与功能证据</h3><ul><li><b>4</b><span>个核心业务页面可交互</span></li><li><b>21 / 21</b><span>遗留表清单完成覆盖</span></li><li><b>7 / 7</b><span>核心 GzStorHist 后端测试通过</span></li><li><b>1 条</b><span>状态与历史版本主链路</span></li></ul><small>来源：参考仓库 README、交接文档与测试报告。它们不是生产 SLA。</small></article>
          <article class="evidence-card"><span class="evidence-tag evidence-tag--target">企业试点需实测</span><h3>建议验收目标</h3><ul><li><b>100%</b><span>关键业务语义一致率</span></li><li><b>&lt; 1s</b><span>查询接口 P95 目标</span></li><li><b>100%</b><span>正式变更具备人类评审</span></li><li><b>≤ 10min</b><span>旧路径恢复目标</span></li></ul><small>目标来自培训讲义；必须在客户环境压测、演练并留证后，才能记为结果。</small></article>
        </div>
        <div class="metric-formulas">
          <div><span>VCR</span><b>Verified Change Rate</b><p>已验证的 Agent 辅助变更 / 所有提交的 Agent 辅助变更</p></div>
          <div><span>LTR</span><b>Lead Time Reduction</b><p>比较基线交付周期与 AI-Native 交付周期</p></div>
          <div><span>DER</span><b>Defect Escape Rate</b><p>比较方法实施前后的生产逃逸缺陷</p></div>
        </div>
      </section>

      <section id="delivery" class="delivery-section section-pad">
        <div class="delivery-heading">
          <p class="eyebrow eyebrow--dark"><span></span> 代码托管与持续交付</p>
          <h2>网站、代码与发布证据，<br />都放在可追踪的位置。</h2>
          <p>站点采用 Vue 3 + TypeScript + Vite，源代码由 GitHub 托管。任何进入 main 的变更都先经过测试与构建，再由 GitHub Actions 发布到 Pages。</p>
        </div>
        <div class="repo-layout">
          <article class="repo-card repo-card--primary">
            <div class="repo-head"><span class="repo-icon">⌘</span><div><small>PUBLIC WEBSITE</small><h3>AI-enable-software-factory</h3></div><span class="live-badge"><i></i> LIVE</span></div>
            <p>面向客户、合作方与团队成员的产品说明、真实案例、方法论与效果证据入口。</p>
            <a href="https://tjluo-ucas.github.io/AI-enable-software-factory/" target="_blank" rel="noreferrer">访问网站 <span>↗</span></a>
          </article>
          <article class="repo-card">
            <div class="repo-head"><span class="repo-icon">&lt;/&gt;</span><div><small>SOURCE &amp; EVIDENCE</small><h3>tjluo-ucas.github.io / app</h3></div></div>
            <p>包含站点源码、可交互案例状态机、自动测试、部署工作流与完整托管说明。</p>
            <a href="https://github.com/tjluo-ucas/tjluo-ucas.github.io/tree/master/app" target="_blank" rel="noreferrer">查看本站源码 <span>↗</span></a>
          </article>
        </div>
        <div class="delivery-flow">
          <div><span>01</span><b>Issue / 需求来源</b><small>为什么改</small></div><i>→</i><div><span>02</span><b>Branch / 候选变更</b><small>改了什么</small></div><i>→</i><div><span>03</span><b>Test + Build</b><small>如何证明</small></div><i>→</i><div><span>04</span><b>Human Review</b><small>谁批准</small></div><i>→</i><div><span>05</span><b>GitHub Pages</b><small>发布结果</small></div>
        </div>
        <div class="delivery-note"><span>部署边界</span><p>GitHub Pages 承载的是产品说明与前端交互演示；企业生产版的 Spring Boot API、数据库、身份认证和审计系统应部署在客户受控环境，不能把静态演示当作生产后端。</p></div>
      </section>

      <section class="closing-cta section-pad">
        <div><p class="eyebrow"><span></span> START WITH EVIDENCE</p><h2>先选一个真实问题，<br />完成一条可验证的迁移闭环。</h2></div>
        <div><p>推荐从只读、低风险、业务价值清晰的遗留模块开始，在 3–6 周内验证方法是否适合你的组织。</p><a class="button button--light" href="https://github.com/tjluo-ucas/tjluo-ucas.github.io/tree/master/app" target="_blank" rel="noreferrer">查看本站实现 <span>↗</span></a></div>
      </section>
    </main>

    <footer>
      <a class="brand brand--footer" href="#top"><span class="brand-mark"><i></i><i></i><i></i></span><span><b>AI-Native</b><small>SOFTWARE FACTORY</small></span></a>
      <p>将遗留知识转化为可验证、可部署、可持续演化的软件资产。</p>
      <div><a href="https://github.com/tjluo-ucas/tjluo-ucas.github.io/blob/master/docs/CASE-STUDY.md" target="_blank" rel="noreferrer">案例说明</a><a href="https://github.com/tjluo-ucas/tjluo-ucas.github.io/tree/master/app" target="_blank" rel="noreferrer">本站源码</a><span>© 2026</span></div>
    </footer>
  </div>
</template>
